/**
 * Product analytics for the caregiver app (GA4 / Firebase Analytics).
 *
 * Reports to the same GA4 property as the marketing site, so the marketing →
 * app journey is one cross-domain funnel. Two gates, mirroring `ads.ts`:
 *   1. **Production hosts only** — one shared GA4 property; keep dev/test out.
 *   2. **Consent** — Consent Mode is denied by default; we only init/log once
 *      the visitor accepts cookies (`window.rmvCookies`, cross-loaded from the
 *      marketing origin). No analytics, no cookies, before consent.
 *
 * GA is lazily initialised on the first eligible event, so importing this
 * module has no side effects and nothing runs in dev or pre-consent.
 *
 * Keep event params free of PII / free-text content (no names, emails, or
 * calendar titles) — names of events and that an action happened, only.
 */

import { initializeAnalytics, logEvent, type Analytics } from 'firebase/analytics';

import { app } from './firebase';
import { isProdHost } from './prodHost';

declare global {
  interface Window {
    rmvCookies?: {
      hasConsent?: () => boolean;
      onConsent?: (cb: () => void) => void;
      /** Re-opens the consent banner — the app's "Manage cookies" link
       * (Settings footer); the privacy policy promises this control. */
      open?: () => void;
    };
  }
}

let analytics: Analytics | null = null;

function instance(): Analytics | null {
  if (!isProdHost()) return null;
  if (!window.rmvCookies?.hasConsent?.()) return null;
  if (!analytics) {
    try {
      // send_page_view: false — we send our own normalized page_view from
      // RouteTracker, so disable GA's automatic one to avoid double-counting.
      analytics = initializeAnalytics(app, { config: { send_page_view: false } });
    } catch {
      return null; // blocked / unsupported / no measurementId
    }
  }
  return analytics;
}

/** Log a product event. No-ops unless on a production host AND the visitor has
 * accepted cookies. Params must be PII-free. */
export function track(name: string, params?: Record<string, unknown>): void {
  const a = instance();
  if (a) logEvent(a, name, params);
}

/** Collapse a concrete route into a stable, id-free screen name so GA groups
 * e.g. every account's Voice page together and no account/calendar ids leak
 * into analytics. */
export function screenName(pathname: string): string {
  const cleaned = pathname
    .replace(/\/accounts\/[^/]+/, '/accounts/:aid')
    .replace(/\/calendars\/[^/]+/, '/calendars/:cid')
    .replace(/\/invites\/[^/]+/, '/invites/:token');
  return cleaned || '/';
}

/** Fire a page_view for an in-app navigation (covers "which sections do they
 * explore"). Called from the router on every route change. */
export function trackPageView(pathname: string): void {
  track('page_view', { page_path: screenName(pathname) });
}
